import isEqual from 'lodash/isEqual';
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import { computed, ref, toValue, watch } from 'vue';

import authorization from '@rygen/rygen-ui/authorization';
import {
  ClientReferenceNumberType,
  DomainReference,
  Loadable,
  Optional,
  ReferenceNumberTypeLevel,
} from '@rygen/rygen-ui/model';
import { distinctBy, notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import {
  EmbeddableLocation,
  FreightAllowance,
  ManualShipment,
  OrderOrShipmentTemplate,
  ShipmentBillTo,
} from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

import { useReferenceNumberTypes } from './useReferenceNumberTypes';

export interface UseNewShipmentWithDefaultsResponse {
  isLoading: ComputedRef<boolean>;
  isDirty: ComputedRef<boolean>;
  shipment: Ref<ManualShipment>;
  shipmentWithDefaults: Ref<ManualShipment>;
}

export const useNewShipmentWithDefaults = (
  selectedTemplate: MaybeRefOrGetter<OrderOrShipmentTemplate | undefined>,
): UseNewShipmentWithDefaultsResponse => {
  const { owningClient } = useParentEntityStore();
  const {
    getDefaultReferenceNumbers,
    lineItemReferenceNumberTypes,
    loadReferenceNumberTypes,
    orderReferenceNumberTypes,
    shipmentReferenceNumberTypes,
  } = useReferenceNumberTypes();

  const shipment: Ref<ManualShipment> = ref(new ManualShipment());
  const shipmentWithDefaults: Ref<ManualShipment> = ref(
    new ManualShipment(shipment.value),
  );
  const _isLoading = ref(false);

  const isLoading = computed(() => _isLoading.value);

  const isDirty = computed(() => {
    return !isEqual(shipment.value, shipmentWithDefaults.value);
  });

  const getDefaultRefNums = async (
    level: ReferenceNumberTypeLevel,
    refNums: Ref<Loadable<ClientReferenceNumberType[]>>,
  ) => {
    await loadReferenceNumberTypes({
      level,
      bypassCache: true,
    });
    return getDefaultReferenceNumbers(refNums.value.data);
  };

  const applyDefaultReferenceNumbers = async () => {
    const results = await Promise.allSettled([
      getDefaultRefNums(
        ReferenceNumberTypeLevel.ORDER,
        orderReferenceNumberTypes,
      ),
      getDefaultRefNums(
        ReferenceNumberTypeLevel.SHIPMENT,
        shipmentReferenceNumberTypes,
      ),
    ]);
    const defaultReferenceNumbers = results
      .map((res) => (res.status === 'fulfilled' ? res.value : undefined))
      .filter(notNull)
      .flat()
      .filter(distinctBy('typeNaturalId'));

    if (defaultReferenceNumbers.length) {
      shipment.value.referenceNumbers = defaultReferenceNumbers;
    }
  };

  const applyDefaultLineItemReferenceNumbers = async () => {
    const defaultLineItemReferenceNumbers = await getDefaultRefNums(
      ReferenceNumberTypeLevel.LINE_ITEM,
      lineItemReferenceNumberTypes,
    );

    if (defaultLineItemReferenceNumbers.length) {
      const lineItem = shipment.value.lineItems[0];
      lineItem.referenceNumbers = defaultLineItemReferenceNumbers;
    }
  };

  const applyDefaultOrderData = async (
    template: Optional<OrderOrShipmentTemplate>,
  ) => {
    if (!authorization.hasAuthority('client', 'read')) {
      return;
    }

    const defaultOrderData = await api.client.getDefaultOrderData();

    if (!shipment.value.billTo || shipment.value.billTo.isEmpty) {
      shipment.value.billTo = defaultOrderData.billToAddress
        ? new ShipmentBillTo({
            location: EmbeddableLocation.fromLocation(
              defaultOrderData.billToAddress,
            ),
            billToDetails: defaultOrderData.billToAddress.billToDetails,
          })
        : new ShipmentBillTo();
    }

    const freightAllowanceEnabledIsDefinedOnTemplate =
      template?.templateData?.freightAllowance?.enabled != null;
    const orderAlreadyHasFreightAllowanceAmount =
      !!shipment.value.freightAllowance?.amount?.amount;
    if (
      !freightAllowanceEnabledIsDefinedOnTemplate &&
      !orderAlreadyHasFreightAllowanceAmount
    ) {
      shipment.value.freightAllowance = new FreightAllowance({
        enabled: defaultOrderData.freightAllowanceEnabled,
      });
    }
  };

  const applyTemplateAndDefaults = async (
    owner?: DomainReference,
    template?: OrderOrShipmentTemplate,
  ) => {
    if (!owner?.id) {
      shipment.value = new ManualShipment();
      shipmentWithDefaults.value = new ManualShipment(shipment.value);
      return;
    }

    if (template?.templateData) {
      shipment.value = new ManualShipment({
        ...(template?.templateData as ManualShipment),
        owner,
      });
      await applyDefaultOrderData(template);
    } else {
      _isLoading.value = true;
      shipment.value = new ManualShipment({ owner });
      await Promise.allSettled([
        applyDefaultReferenceNumbers(),
        applyDefaultLineItemReferenceNumbers(),
        applyDefaultOrderData(template),
      ]);
      _isLoading.value = false;
    }

    shipmentWithDefaults.value = new ManualShipment(shipment.value);
  };

  watch(
    () => toValue(selectedTemplate),
    (newVal) => {
      if (!isEqual(shipment.value, newVal?.templateData)) {
        applyTemplateAndDefaults(owningClient.value, newVal);
      }
    },
    { immediate: true },
  );

  watch(
    owningClient,
    (newVal: Optional<DomainReference>, oldVal: Optional<DomainReference>) => {
      if (newVal?.id === oldVal?.id) {
        return;
      }

      applyTemplateAndDefaults(newVal, toValue(selectedTemplate));
    },
    { immediate: true },
  );

  return {
    isLoading,
    isDirty,
    shipment,
    shipmentWithDefaults,
  };
};
